export const AUTH0_SIGNUP_PARAM = 'auth0Signup';

/**
 * Client-portal route for the public single-use video join link. Possession of the link is the only
 * credential, so this page must never authenticate: AuthTokenService skips the Auth0 flow for it.
 * Compared against the pathname alone, never substring-matched against a full URL.
 */
export const DIRECT_VIDEO_PATH = '/video/direct';

/**
 * Global set on the server-rendered admin page by sonder-rails when the admin has an
 * Auth0 session (see app/views/shared/_admin_auth0_boot.html.slim). Holds the Auth0
 * access token the Rails session obtained, so the embedded admin Angular widgets can
 * authenticate against the monolith API without running an in-browser Auth0 login.
 * This is the contract between the Rails admin shell and the admin portal SPA.
 */
export const ADMIN_AUTH0_TOKEN_GLOBAL = 'SONDERMIND_ADMIN_AUTH0_TOKEN';

/**
 * Query param marker indicating a returning user signed in from the public quick-checkout
 * "Sign in" link and should be deep-linked into the client-portal direct-booking flow.
 * Set on the Auth0 redirect_uri by the provider-landing app and read by client-portal post-login.
 */
export const BOOK_INTENT_PARAM = 'bookIntent';

/**
 * Query param keys carrying the booking-intent payload across the cross-domain Auth0 login.
 * Written on the redirect_uri by the public quick-checkout "Sign in" link and read by
 * client-portal post-login to open the direct-booking modal for the same provider/slot.
 */
export enum BookIntentParam {
  PROVIDER_ID = 'bookProviderId',
  START = 'bookStart',
  END = 'bookEnd',
  SESSION_TYPE = 'bookSessionType'
}

/**
 * sessionStorage key marking that client-portal's SsoGuard has started an IdP login. Only the guard
 * writes it, and it is written after Auth0TokenService.initializeAuth0() has already run, so it is
 * absent on the outbound leg of a first SSO attempt. It is meaningful only on the return leg, where
 * Auth0 bounces back with `error=login_required` and no `sso=` param to identify the attempt.
 */
export const SSO_IN_FLIGHT_KEY = 'sso_in_flight';

/**
 * One-shot sessionStorage marker set by the member-portal signup launcher immediately before it
 * hands a provider off to Auth0 hosted signup, and consumed by AuthTokenService on the return leg.
 *
 * `/signup/` is on the Auth0 skip list because the legacy wizard must render for an
 * unauthenticated provider without initializeAuth0() force-redirecting them to Auth0 login. But
 * after hosted signup the logged-in guard bounces the provider back to `/signup/<code>` with a
 * full page load, and the skip means the Auth0 session is never read: the launcher sees
 * `loggedIn === false`, resolves the strategy again, and re-enters signup for an account Auth0
 * just created ("Looks like you already have an account with this email").
 *
 * This marker lets that ONE return leg initialize Auth0 on `/signup/`. It is cleared as soon as it
 * is read, so the flag-off path, old invite links and a fresh first visit are all unchanged.
 */
export const PROVIDER_SIGNUP_AUTH0_RETURN_KEY = 'providerSignupAuth0Return';

/**
 * sessionStorage key carrying the `error_description` of an Auth0 `access_denied` callback across
 * the logout that `Auth0TokenService` performs to break the redirect loop. An Auth0 Action's
 * `api.access.deny(reason)` is the only channel a deny has to the user, and the logout's
 * `returnTo` is the bare origin, so without this the reason is lost and the user sees a blank
 * login start. Consumers read it once and remove it.
 */
export const AUTH0_ACCESS_DENIED_REASON_KEY = 'auth0AccessDeniedReason';

/**
 * sessionStorage key holding the same-origin path the access_denied logout returns to. The default
 * `returnTo` is the bare origin, which lands an unauthenticated user back on the Auth0 login screen
 * before any page that could read `AUTH0_ACCESS_DENIED_REASON_KEY` renders. The provider signup
 * launcher stores its invite path here before the handoff; `Auth0TokenService` consumes it once.
 */
export const AUTH0_ACCESS_DENIED_RETURN_PATH_KEY = 'auth0AccessDeniedReturnPath';
